import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Check, Megaphone, CreditCard, Users } from "lucide-react";

export default function ClubDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [club, setClub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);

  useEffect(() => {
    api.get(`/restaurants/${id}`).then((r) => setClub(r.data)).finally(() => setLoading(false));
  }, [id]);

  const join = async () => {
    if (!user) { navigate("/login"); return; }
    setJoining(true);
    try {
      const { data } = await api.post("/checkout/subscribe", {
        restaurant_id: id, origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(apiErr(err));
      setJoining(false);
    }
  };

  if (loading) return <div className="App"><Navbar /><p className="p-10">Loading...</p></div>;
  if (!club) return <div className="App"><Navbar /><p className="p-10">Club not found.</p></div>;

  return (
    <div className="App min-h-screen bg-[#FFF4E6] text-[#17324D]">
      <Navbar />
      <div className="max-w-5xl mx-auto px-5 py-12 grid md:grid-cols-5 gap-8">
        {/* Left: flyer */}
        <div className="md:col-span-3">
          <span className="inline-block text-xs font-bold tracking-[0.18em] uppercase text-[#D94A3D] mb-3">
            Regulars Club
          </span>
          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05]">{club.name}</h1>
          <p className="text-base md:text-lg text-[#17324D]/70 mt-3">{club.description}</p>
          <p data-testid="detail-member-count" className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-[#17324D] bg-white border border-[#17324D]/10 rounded-full px-3 py-1.5">
            <Users size={15} className="text-[#D94A3D]" />
            {club.member_count > 0 ? `${club.member_count} regular${club.member_count === 1 ? "" : "s"} already joined` : "Be the first regular to join"}
          </p>

          <div className="mt-8 space-y-3">
            <h2 className="font-heading font-bold text-xl">What you get</h2>
            {(club.perks || []).map((p, i) => (
              <div key={i} data-testid={`detail-perk-${i}`} className="card-brut rounded-xl p-4 flex items-center gap-3 bg-white">
                <span className="bg-[#D94A3D]/10 text-[#D94A3D] p-1.5 rounded-lg"><Check size={18} strokeWidth={3} /></span>
                <span className="font-medium">{p}</span>
              </div>
            ))}
          </div>

          {(club.weekly_deals || []).length > 0 && (
            <div className="mt-8">
              <h2 className="font-heading font-bold text-xl flex items-center gap-2">
                <Megaphone size={24} className="text-[#D94A3D]" /> Weekly Member Deals
              </h2>
              <ul className="mt-3 space-y-2">
                {club.weekly_deals.map((d, i) => (
                  <li key={i} className="text-[#17324D]/80 flex gap-2"><span className="text-[#D94A3D] font-bold">★</span>{d}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Right: pricing */}
        <div className="md:col-span-2">
          <div className="rounded-2xl p-7 bg-[#17324D] text-white sticky top-24 shadow-[0_8px_30px_rgba(23,50,77,0.15)]">
            <p className="text-xs font-bold tracking-[0.18em] uppercase text-white/70">Membership</p>
            <div className="flex items-baseline gap-1 mt-2">
              <span className="font-heading font-extrabold text-5xl text-white">${club.monthly_price}</span>
              <span className="text-lg">/month</span>
            </div>
            <p className="text-white/70 text-sm mt-1">Auto-renews monthly. Cancel anytime.</p>
            {club.active === false ? (
              <div data-testid="club-paused-notice" className="mt-6 p-4 rounded-xl bg-white/10 text-sm text-white/90">
                This club is currently paused and not accepting new members. Existing members keep their perks.
              </div>
            ) : (
              <>
                <button data-testid="join-club-btn" onClick={join} disabled={joining}
                  className="w-full mt-6 py-3 bg-[#D94A3D] text-white font-semibold rounded-xl hover:bg-[#C33E32] flex items-center justify-center gap-2 disabled:opacity-60 transition-colors">
                  <CreditCard size={18} /> {joining ? "Redirecting..." : "Join & Start Saving"}
                </button>
                <p className="text-xs text-white/60 mt-3 text-center">Credit card required. Secured by Stripe.</p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
